import { LocaleFlag } from "./locale-flag";
import { Select, SelectContent, SelectItem, SelectTriggerBare, SelectValue } from "./select";
import { cn } from "../lib/utils";

export interface LocaleMenuOption {
  /** Locale code, e.g. `et`, `en`, `pt-BR`. */
  code: string;
  /** The language's own name, shown in the list. */
  label: string;
}

export interface LocaleMenuProps {
  value: string;
  options: LocaleMenuOption[];
  onChange: (code: string) => void;
  /** Accessible name of the pill button ("Switch language"). */
  triggerLabel: string;
  /** Accessible name of the open list ("Languages"). */
  listLabel?: string;
  className?: string;
}

function baseCode(code: string) {
  return code.split("-")[0] ?? code;
}

/**
 * The UI-language pill of the account hub and the CRM: flag + short code + chevron,
 * opening a list of flag + name + check. It mirrors the public site's
 * `LanguageSwitcher`, so the apps and the front end read as one product.
 *
 * Built on the kit Select, so the list gets Radix's keyboard handling, typeahead and
 * focus return, and opens scrolled to the active language. The flags are SVGs from
 * `LocaleFlag`, never emoji: Windows does not draw regional-indicator flags.
 */
export function LocaleMenu({ value, options, onChange, triggerLabel, listLabel, className }: LocaleMenuProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      {/* Its own data-slot, not "select-trigger": an app that dresses every form
          select as a field (the CRM paints them as sunken pills) must not dress
          this header pill too. */}
      <SelectTriggerBare
        data-slot="locale-menu-trigger"
        aria-label={triggerLabel}
        className={cn(
          "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-transparent px-2.5",
          "text-[13px] font-medium text-foreground transition-colors hover:bg-accent",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
          "[&>svg]:size-3 [&>svg]:transition-transform data-[state=open]:[&>svg]:rotate-180",
          className,
        )}
      >
        {/* Custom children: the pill shows the short code, not the list's full name. */}
        <SelectValue>
          <span className="inline-flex items-center gap-1.5">
            <LocaleFlag locale={baseCode(value)} className="w-[18px]" />
            <span>{baseCode(value).toUpperCase()}</span>
          </span>
        </SelectValue>
      </SelectTriggerBare>
      <SelectContent
        align="end"
        aria-label={listLabel}
        className="max-h-[min(48vh,340px)] w-auto min-w-40 rounded-xl"
      >
        {options.map((o) => (
          <SelectItem key={o.code} value={o.code} className="py-2 pl-3 data-[state=checked]:text-primary">
            <span className="flex items-center gap-2.5">
              <LocaleFlag locale={o.code} className="w-5" />
              {o.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
